import {
  Component,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { StorefrontService } from '../../services/storefront.service';
import { CommonModule } from '@angular/common';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';

import type { Storefront } from '../../services/storefront.service';
import { CompanyGroupService, RedemptionLocation } from '../../services/company-group.service';
import { LoggerService } from '../../services/logger.service';
import { RedemptionMapComponent } from './components/redemption-map/redemption-map.component';

@Component({
  selector: 'app-storefront',
  standalone: true,
  imports: [CommonModule, RedemptionMapComponent],
  templateUrl: './storefront.component.html',
  styleUrl: './storefront.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StorefrontComponent implements OnInit, OnDestroy {
  @ViewChild('storeDescription') storeDescriptionRef?: ElementRef<HTMLParagraphElement>;
  storeInformation: Storefront | undefined = undefined;
  errorMessage: string | null = null;
  subscriptions: Subscription[] = [];
  redemptionLocations: RedemptionLocation[] = [];
  /** The shop's own pin, shown when it has no Verbund members to list. */
  storeLocation: RedemptionLocation | null = null;
  locationsLoaded = false;
  descriptionExpanded = false;
  isTruncated = false;
  truncatedText = '';
  private resizeObserver?: ResizeObserver;

  constructor(
    private storefrontService: StorefrontService,
    private groupService: CompanyGroupService,
    private titleService: Title,
    private logger: LoggerService,
  ) {}

  /** Only this page uses the tinted canvas — see body.storefront-page. */
  private static readonly BODY_CLASS = 'storefront-page';

  ngOnInit(): void {
    document.body.classList.add(StorefrontComponent.BODY_CLASS);
    this.loadStoreData();
  }

  loadStoreData() {
    const companyName = this.storefrontService.getStorefrontCompany();

    if (!companyName) {
      this.logger.error('❌ [StorefrontComponent] No company name found, showing error');
      this.errorMessage = 'Unable to determine company from URL';
      return;
    }

    this.subscriptions.push(
      this.storefrontService.getStoreBySubdomain(companyName).subscribe({
        next: (storeData) => {
          this.storeInformation = storeData;
          this.reportVisit(companyName);
          this.titleService.setTitle(`${this.storeInformation.name} – Gutscheine`);
          this.errorMessage = null;
          this.descriptionExpanded = false;
          this.isTruncated = false;
          this.truncatedText = '';
          setTimeout(() => this.initDescriptionTruncation());

          this.locationsLoaded = false;
          this.storeLocation = this.toLocation(storeData);
          this.subscriptions.push(
            this.groupService
              .getRedemptionLocations(storeData.public_id)
              .pipe(catchError(() => of([])))
              .subscribe((locations) => {
                // Only the shop itself: its own map below, not a list of one
                const onlyItself =
                  locations.length === 1 && locations[0].company_id === storeData.public_id;
                this.redemptionLocations = onlyItself ? [] : locations;
                this.locationsLoaded = true;
              }),
          );
        },
        error: (error) => {
          this.logger.error('❌ [StorefrontComponent] Error loading store data:', error);
          if (error.status === 404) {
            this.errorMessage = 'Sorry,this store is not registered yet :(';
          } else {
            this.errorMessage = 'An error occurred while loading the store';
          }
          this.storeInformation = undefined;
        },
      }),
    );
  }

  /** A visitor who came through a link from elsewhere: the backend checks whether it
   * was the company's website. Fire and forget, the page doesn't depend on it. */
  private reportVisit(store: string): void {
    const referrer = document.referrer;
    if (!referrer) return;
    try {
      if (new URL(referrer).host === window.location.host) return;
    } catch {
      return;
    }
    this.storefrontService
      .reportVisit(store, referrer)
      .pipe(catchError(() => of(undefined)))
      .subscribe();
  }

  /** Step icons use the company's widget branding colors, falling back to the Schenkwerk default. */
  get stepIconStyle(): Record<string, string> {
    const primary = this.storeInformation?.voucher_app_primary_color || '#4b5ae4';
    const text = this.storeInformation?.voucher_app_text_color || '#FFFFFF';
    return {
      'background-color': primary,
      color: text,
      'box-shadow': `0 4px 10px ${this.hexToRgba(primary, 0.25)}`,
    };
  }

  private hexToRgba(hex: string, alpha: number): string {
    const value = hex.replace('#', '');
    const full =
      value.length === 3
        ? value
            .split('')
            .map((c) => c + c)
            .join('')
        : value;
    const num = parseInt(full, 16);
    if (!Number.isFinite(num)) return `rgba(75, 90, 228, ${alpha})`;
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
    if (!this.descriptionExpanded) {
      setTimeout(() => this.truncateDescription());
    }
  }

  private initDescriptionTruncation(): void {
    const el = this.storeDescriptionRef?.nativeElement;
    if (!el || !this.storeInformation?.description) return;
    this.truncateDescription();
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.descriptionExpanded) {
        this.truncateDescription();
      }
    });
    this.resizeObserver.observe(el);
  }

  /**
   * Shortens the description to at most two lines. The "mehr anzeigen" toggle stays
   * inline right after the cut-off text, so the cut point is found by measuring a
   * hidden clone that contains the text plus the ellipsis and the toggle itself.
   */
  private truncateDescription(): void {
    const description = this.storeInformation?.description ?? '';
    this.truncatedText = description;
    this.isTruncated = false;
    if (!description) return;

    const el = this.storeDescriptionRef?.nativeElement;
    if (!el) return;

    const measurer = this.buildMeasurer(el);
    const maxHeight = this.getLineHeight(el) * 2 + 1;

    if (this.fitsWithin(measurer, description, maxHeight, false)) {
      measurer.remove();
      return;
    }

    this.isTruncated = true;

    // longest prefix that still fits together with "…" and the toggle
    let lo = 0;
    let hi = description.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (this.fitsWithin(measurer, this.cutAtWordBoundary(description, mid), maxHeight, true)) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    this.truncatedText = this.cutAtWordBoundary(description, lo);
    measurer.remove();
  }

  private buildMeasurer(el: HTMLElement): HTMLElement {
    const measurer = document.createElement('p');
    measurer.className = 'store-description';
    this.copyScopeAttributes(el, measurer);
    measurer.setAttribute('aria-hidden', 'true');
    measurer.style.cssText = `position:absolute;visibility:hidden;left:-9999px;top:0;width:${el.clientWidth}px;`;
    document.body.appendChild(measurer);
    return measurer;
  }

  private fitsWithin(
    measurer: HTMLElement,
    text: string,
    maxHeight: number,
    withSuffix: boolean,
  ): boolean {
    const children: Node[] = [document.createTextNode(text)];
    if (withSuffix) {
      const ellipsis = document.createElement('span');
      ellipsis.className = 'desc-ellipsis';
      ellipsis.textContent = '…';
      this.copyScopeAttributes(measurer, ellipsis);
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'description-toggle';
      this.copyScopeAttributes(measurer, toggle);
      toggle.innerHTML =
        'mehr anzeigen <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>';
      children.push(ellipsis, toggle);
    }
    measurer.replaceChildren(...children);
    return measurer.scrollHeight <= maxHeight;
  }

  /** Angular scopes component styles via _ngcontent attributes – copy them so the clone measures identically. */
  private copyScopeAttributes(source: HTMLElement, target: HTMLElement): void {
    for (const name of source.getAttributeNames()) {
      if (name.startsWith('_ngcontent')) {
        target.setAttribute(name, source.getAttribute(name) ?? '');
      }
    }
  }

  private getLineHeight(el: HTMLElement): number {
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    if (Number.isFinite(lineHeight)) return lineHeight;
    return parseFloat(getComputedStyle(el).fontSize) * 1.6;
  }

  /** Trims a prefix to the last word boundary so the text is never cut mid-word. */
  private cutAtWordBoundary(text: string, length: number): string {
    if (length >= text.length) return text;
    const prefix = text.slice(0, length);
    const lastSpace = prefix.lastIndexOf(' ');
    return lastSpace > 0 ? prefix.slice(0, lastSpace) : prefix;
  }

  ngOnDestroy() {
    document.body.classList.remove(StorefrontComponent.BODY_CLASS);
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.resizeObserver?.disconnect();
  }

  getImageUrl(imagePath: string): string {
    return this.storefrontService.getImageUrl(imagePath);
  }

  private toLocation(store: Storefront): RedemptionLocation | null {
    if (store.latitude == null || store.longitude == null) return null;
    return {
      id: store.public_id,
      company_id: store.public_id,
      company_name: store.name,
      street: store.street ?? null,
      zip: store.zip ?? null,
      city: store.city ?? null,
      has_pin: false,
      logo_image_path: store.logo_image_path ?? null,
      latitude: store.latitude,
      longitude: store.longitude,
      website: store.website ?? null,
    };
  }
}
